import { Suspense, lazy, useEffect, type ComponentType, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { destinoInicialDoFlow } from '../core/produtos';
import { useAuth } from '../core/auth';
import { RouteGuard } from '../core/RouteGuard';
import { EsqueletoDaCasca, EsqueletoDeAcesso, formaDaRota } from '../ui/esqueletos';
import { comSlug, useNavigate, useSlugDoCliente } from '../core/rotas';
import {
  PAINEL,
  clienteDoHost,
  destinoDoSuitemailNoApex,
  ehLocalhost,
  ehRaizDoPainel,
  superficieDoHost,
  type Superficie,
} from '../core/dominios';
import type { AppCode, Permission } from '../core/permissions';
import { Shell } from '../shell/Shell';
import { ErrorBoundary } from '../shell/ErrorBoundary';
import { PonteParaOMeet, ReceberDeSubdominio, saltarParaSubdominio } from '../pages/auth/HandoffSubdominio';
import { Button } from '../ui';
import { Icone } from '../ui/icones';

/**
 * As telas chegam quando são pedidas.
 *
 * Antes este arquivo importava as 34 telas de uma vez, e o Vite não tinha como
 * separá-las: tudo o que a raiz importa, a raiz carrega. O resultado era um
 * arquivo único de 1068 kB, e quem abria a TELA DE LOGIN baixava o CRM inteiro,
 * o editor de e-mail, o construtor de quiz e o console da Yard antes de poder
 * digitar a senha. Numa conexão de celular isso são segundos de tela branca
 * para ver um formulário de dois campos.
 *
 * Com `import()` dinâmico cada área vira um pedaço próprio, buscado no primeiro
 * clique que leva até ela e guardado em cache depois disso.
 *
 * O que continua CHEGANDO JUNTO, de propósito: as telas de entrada, os estados
 * de erro e o casco. São o caminho que todo mundo percorre, e adiar isso só
 * trocaria a espera de lugar.
 */
type Qualquer = ComponentType<Record<string, never>>;

/** Uma tela solta de um módulo. */
const tela = (carregar: () => Promise<Record<string, unknown>>, nome: string) =>
  lazy(async () => ({ default: (await carregar())[nome] as Qualquer }));

/**
 * Várias telas do MESMO módulo, com a forma de sempre (`Crm.Pipeline`).
 *
 * Preserva a escrita das rotas, que é o que importa aqui: o corpo do arquivo
 * não muda uma linha, então o que estava certo continua certo.
 */
const telas = <N extends string>(
  carregar: () => Promise<Record<string, unknown>>,
  nomes: readonly N[],
) => {
  const saida = {} as Record<N, Qualquer>;
  for (const nome of nomes) saida[nome] = tela(carregar, nome);
  return saida;
};

// ── chegam junto: o caminho de todo mundo ──────────────────────────────────
import { AcceptInvite, CreateOrg, ForgotPassword, Login, ResetPassword, Signup } from '../pages/auth/Auth';
import { Forbidden, NotFound, ServerError, Suspended } from '../pages/auth/States';

// ── chegam quando pedidas ──────────────────────────────────────────────────
const Home = tela(() => import('../pages/Home'), 'Home');
const Site = tela(() => import('../pages/public/Site'), 'Site');

/**
 * O yard suitemail é outro produto, e por isso é outra árvore de rotas.
 *
 * Ele não pendura no `<Shell />` do Suite: tem casca própria, porta própria e
 * navegação própria. Pelo Suite só se cria a conta e se vincula a caixa; ler
 * e-mail, mover cartão na triagem e entrar na reunião acontece só aqui. Ver
 * `docs/migracao/E-suitemail-arquitetura.md`.
 */
const Suitemail = tela(() => import('../suitemail/Suitemail'), 'Suitemail');
const ResumoDaReuniao = tela(() => import('../suitemail/reuniao/PosReuniao'), 'RotaDePosReuniao');

/**
 * A galeria de telas. Só existe fora de produção.
 *
 * `import.meta.env.PROD` é resolvido pelo Vite em tempo de compilação: no
 * pacote que sobe, a condição vira `false` literal, o ramo inteiro é removido e
 * o `import()` nunca é emitido. Não é uma checagem que alguém possa contornar
 * pela URL; é código que não existe lá.
 */
const Galeria = import.meta.env.DEV
  ? tela(() => import('../ds/Galeria'), 'Galeria')
  : null;

const PortalPublico = telas(() => import('../pages/mail/Publicas'), [
  'AgendarPublico', 'DocumentoPublico', 'SalaPublica',
] as const);

/**
 * A Reunião: rota própria, fora do `<Shell />` e fora do suitemail
 * (`suitemail.html` §11.1). Atende quem está logado (a organização dona da
 * reunião, sem lobby) e quem chega só com o link (convidado, com lobby se a
 * sala tiver). Ver `docs/mail/reuniao-pre-entrada.json`.
 */
const RotaDeReuniao = tela(() => import('../pages/reuniao/Reuniao'), 'RotaDeReuniao');

/* `Negocios` é a casca inteira do CRM: um app, nove módulos por âncora de
   URL (`ROTAS-CRM.md`, seção 0). `Crm.tsx` - nove telas de rota própria -
   e as duas telas satélite (`SalesAutomationBuilder`, um construtor visual
   de fluxo que o documento explicitamente descarta nesta fase; `Disparos`,
   disparo de e-mail em massa, fora do escopo dos nove módulos) saem do
   roteador. Os arquivos continuam no repositório, sem rota - decisão
   registrada no relatório da refatoração, não código morto por descuido. */
const Negocios = tela(() => import('../pages/crm/Negocios'), 'Negocios');

const Flw = telas(() => import('../pages/flw/Flw'), [
  'Channels', 'ChatbotEditor', 'ChatbotTestar', 'Chatbots', 'FlwDashboard', 'FlwReports', 'Inbox',
] as const);
const FlwCfg = telas(() => import('../pages/flw/FlwConfig'), [
  'AiAgents', 'Campaigns', 'MateriaisDoRobo', 'TabelasDePrecosCima', 'QuickReplies', 'Routing', 'Templates',
] as const);
const Coexistencia = tela(() => import('../pages/flw/Coexistencia'), 'Coexistencia');

const Mkt = telas(() => import('../pages/mkt/Mkt'), [
  'Automations', 'ContactDetail', 'Contacts', 'Dashboard', 'Emails', 'Forms', 'LandingPages',
  'MktSettings', 'Popups', 'Reports', 'Segments',
] as const);
// A moldura do Marketing (abas do módulo) recebe filhos, então não passa pelo
// `telas`, que tipa componente sem props.
const MktFrame = lazy(() => import('../pages/mkt/Mkt').then((m) => ({ default: m.Frame })));
const Jornadas = tela(() => import('../pages/mkt/Jornadas'), 'Jornadas');
const JornadaDoContato = tela(() => import('../pages/mkt/Jornadas'), 'JornadaDoContato');
const EmailRelatorio = tela(() => import('../pages/mkt/EmailRelatorio'), 'EmailRelatorio');
const ImportWizard = tela(() => import('../pages/mkt/editors/Editors'), 'ImportWizard');
const LandingEditor = tela(() => import('../pages/mkt/editors/Editors'), 'LandingEditor');
const AutomationBuilder = tela(() => import('../pages/mkt/editors/AutomationBuilder'), 'AutomationBuilder');
const AutomationViewer = tela(() => import('../pages/mkt/editors/AutomationBuilder'), 'AutomationViewer');
const EmailBuilder = tela(() => import('../pages/mkt/editors/EmailBuilder'), 'EmailBuilder');
const SenderSettings = tela(() => import('../pages/mkt/editors/EmailBuilder'), 'SenderSettings');
const FormBuilder = tela(() => import('../pages/mkt/editors/FormBuilder'), 'FormBuilder');

/**
 * A biblioteca de e-mails - `/{cliente}/emails`, família roxo, o mesmo app
 * MKT (`ROTAS-EMAILS.md`). Não é a campanha (`Mkt.Emails`, acima): é o
 * catálogo dos dez templates com status derivado, prévia real, teste
 * simulado, histórico e assinaturas - a Campanha e a Recuperação de senha só
 * linkam de volta para os fluxos que já existem.
 */
const EmailsBiblioteca = tela(() => import('../pages/emails/Biblioteca'), 'Biblioteca');
const EmailsEditor = tela(() => import('../pages/emails/Editor'), 'EditorDeEmail');
const EmailsTeste = tela(() => import('../pages/emails/Teste'), 'TesteDeEnvio');
const EmailsHistorico = tela(() => import('../pages/emails/Historico'), 'Historico');
const EmailsAssinaturas = tela(() => import('../pages/emails/Assinaturas'), 'Assinaturas');

const Settings = telas(() => import('../pages/platform/Settings'), [
  'ApiKeys', 'Apps', 'AuditScreen', 'Billing', 'Branding', 'Flags',
  'Integrations', 'Members', 'OrganizationSettings', 'Profile', 'Security',
] as const);
const YardClientes = tela(() => import('../pages/platform/Yard'), 'YardClientes');
const YardAuditoria = tela(() => import('../pages/platform/Console'), 'YardAuditoria');
const YardCliente = tela(() => import('../pages/platform/Console'), 'YardCliente');
const YardEquipe = tela(() => import('../pages/platform/Console'), 'YardEquipe');
const YardRecursos = tela(() => import('../pages/platform/Console'), 'YardRecursos');
const YardUsuarios = tela(() => import('../pages/platform/Console'), 'YardUsuarios');
const YardVisao = tela(() => import('../pages/platform/Console'), 'YardVisao');

const Pop = telas(() => import('../pages/pop/Pop'), [
  'PopAparencia', 'PopAtendentes', 'PopConversas', 'PopDashboard', 'PopInstalacao',
  'PopMensagens', 'PopRelatorio',
] as const);

const Bio = telas(() => import('../pages/bio/Bio'), [
  'SiteConfig', 'SiteEditor', 'SiteLinks', 'SiteOverview', 'SiteRelatorios',
  'SiteTema', 'Sites',
] as const);
const BioCom = telas(() => import('../pages/bio/BioComercio'), [
  'QuizBuilder', 'QuizRelatorio', 'SiteAgenda', 'SiteDepoimentos', 'SiteImoveis',
  'SitePedidos', 'SiteProdutos', 'SiteQuizzes',
] as const);

const PublicForm = tela(() => import('../pages/public/Public'), 'PublicForm');
const PublicLanding = tela(() => import('../pages/public/Public'), 'PublicLanding');
const DemoFormulario = tela(() => import('../pages/public/Demo'), 'DemoFormulario');
const DemoPop = tela(() => import('../pages/public/DemoPop'), 'DemoPop');

const AgendaPage = tela(() => import('../pages/public/BioPublic'), 'AgendaPage');
const BioPage = tela(() => import('../pages/public/BioPublic'), 'BioPage');
const CarrinhoPage = tela(() => import('../pages/public/BioPublic'), 'CarrinhoPage');
const CheckoutPage = tela(() => import('../pages/public/BioPublic'), 'CheckoutPage');
const InfoPage = tela(() => import('../pages/public/BioPublic'), 'InfoPage');
const LinkRedirect = tela(() => import('../pages/public/BioPublic'), 'LinkRedirect');
const PedidoPage = tela(() => import('../pages/public/BioPublic'), 'PedidoPage');
const ProdutoPage = tela(() => import('../pages/public/BioPublic'), 'ProdutoPage');
const QuizPage = tela(() => import('../pages/public/BioPublic'), 'QuizPage');
const ShopPage = tela(() => import('../pages/public/BioPublic'), 'ShopPage');

/**
 * O que aparece enquanto o pedaço da tela chega: o esqueleto no formato da
 * tela que vem (`ui/esqueletos.tsx`), nunca um spinner solto. Esta fronteira
 * só pega a primeira carga e as rotas fora da casca; dentro do painel, o
 * `Suspense` do `Shell` segura o miolo e o topo e o dock ficam onde estão.
 * Em página pública não há casca a imitar: fica em branco, sem pular.
 */
const ROTA_DE_ACESSO = /^\/(login|yard|entrar|criar-conta|esqueci-senha|redefinir-senha|aceitar-convite|criar-organizacao)(\/|$)/;
const ROTA_PUBLICA = /^\/(site|b|i|s|p|f|d|dp|l|agendar|doc|_sso|_ponte-meet)(\/|$)/;
const Carregando = () => {
  const { pathname } = useLocation();
  if (ROTA_DE_ACESSO.test(pathname)) return <EsqueletoDeAcesso />;
  if (ROTA_PUBLICA.test(pathname) || superficieDoHost(window.location.hostname)) {
    return <div className="state" aria-busy="true" />;
  }
  return <EsqueletoDaCasca forma={formaDaRota(pathname)} />;
};

/** Envolve a tela na cadeia de guardas do documento 14.3. */
const G = ({
  app,
  permission,
  soYard,
  children,
}: {
  app?: AppCode;
  permission?: Permission;
  soYard?: boolean;
  children: ReactNode;
}) => (
  <RouteGuard
    {...(app ? { app } : {})}
    {...(permission ? { permission } : {})}
    {...(soYard ? { soYard } : {})}
  >
    {children}
  </RouteGuard>
);

/**
 * Em domínio público o caminho é o handle, e ele fica na barra como está.
 *
 * Nada de redirecionar `/cima` para `/b/cima`: o endereço curto É o endereço.
 * Cada domínio monta a sua própria árvore de rotas, sem prefixo, e o prefixo
 * interno passa a existir só dentro do painel.
 *
 * Link antigo com prefixo (`myard.bio/b/cima`) é limpo por 301 no server.js,
 * antes de o aplicativo carregar.
 */
function RotasPublicas({ superficie }: { superficie: Superficie }) {
  if (superficie === 'info') {
    return (
      <Routes>
        <Route path="/:handle" element={<InfoPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  if (superficie === 'shop') {
    return (
      <Routes>
        <Route path="/:handle" element={<ShopPage />} />
        <Route path="/:handle/p/:slug" element={<ProdutoPage />} />
        <Route path="/:handle/carrinho" element={<CarrinhoPage />} />
        <Route path="/:handle/checkout" element={<CheckoutPage />} />
        <Route path="/:handle/pedido/:token" element={<PedidoPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/:handle" element={<BioPage />} />
      <Route path="/:handle/q/:slug" element={<QuizPage />} />
      <Route path="/:handle/agenda/:slug" element={<AgendaPage />} />
      <Route path="/:handle/l/:slug" element={<LinkRedirect />} />
      {/* Formato do painel (`/l/{handle}/{slug}`) chegando em domínio público.
          O server.js faz 301 quando vê o Host certo, mas a CDN da Hostinger
          na frente de myard.bio troca o Host e o Node serve a SPA: o
          redirecionamento precisa existir aqui também. */}
      <Route path="/l/:handle/:slug" element={<LinkRedirect />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export function Router() {
  const { hostname, pathname } = window.location;
  const superficie = superficieDoHost(hostname);

  // A fronteira do carregamento preguiçoso fica AQUI, e uma vez só: colocada em
  // cada rota, cada troca de tela piscaria o proprio indicador, e a impressao
  // seria de aplicacao recarregando em vez de navegando.
  const comEspera = (arvore: ReactNode) => (
    <Suspense fallback={<Carregando />}>{arvore}</Suspense>
  );

  if (superficie === null) return comEspera(<Rotas />);

  // Raiz de domínio público não é página de ninguém: ninguém se chama "nada".
  // O 301 do server.js já resolve; isto cobre navegação dentro do aplicativo.
  // Em máquina local a raiz não sai para o domínio público: fica no próprio endereço.
  const local = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
  if (pathname.replace(/^\/+|\/+$/g, '') === '' && !local) {
    window.location.replace(PAINEL);
    return null;
  }

  return comEspera(<RotasPublicas superficie={superficie} />);
}

/**
 * Redirecionamento DENTRO da área do cliente.
 *
 * `Navigate to="/mkt/contacts"` levava ao caminho literal, sem o slug, e desde
 * que o painel passou a morar sob o slug isso deixou de existir: `/cima/mkt`
 * caía em `/mkt/contacts`, que o roteador lê como o cliente de slug "mkt". O
 * resultado era "não encontrado" ao clicar em Marketing, CRM, Flow ou
 * Configurações: os quatro atalhos do menu que não apontam para uma tela final.
 *
 * Aqui o slug entra de volta, pela mesma função que `Link` e `useNavigate` usam.
 */
function EntradaDoFlow() {
  const { permissions } = useAuth();
  return <VaiPara to={destinoInicialDoFlow(permissions)} />;
}

function VaiPara({ to }: { to: string }) {
  const slug = useSlugDoCliente();
  return <Navigate to={comSlug(to, slug)} replace />;
}

/**
 * `yardsuite.com.br/{slug}/...` em produção: salto para o subdomínio do
 * cliente (ver o comentário na rota, em `Rotas`). Efeito, não render - troca
 * a página inteira, como `RaizDoPainel` faz com a raiz.
 */
function SaltarParaOCliente() {
  const { slug = '', '*': resto = '' } = useParams();
  const { search, hash } = useLocation();
  const { status, org, orgs } = useAuth();
  const conhecido = status === 'ready' && (org?.slug === slug || orgs.some((o) => o.slug === slug));
  const destino = `/${resto}${search}${hash}`;

  useEffect(() => {
    if (conhecido) void saltarParaSubdominio(slug, destino);
  }, [conhecido, slug, destino]);

  if (status === 'loading' || conhecido) return null;
  return <NotFound />;
}

/**
 * Para onde vai quem abre a raiz.
 *
 * Antes era um redirecionamento fixo para `/home`, que não existe mais sozinho:
 * o painel agora mora sob o slug do cliente. Quem tem organização ativa vai
 * para a dele; a equipe Yard, que pode não ter nenhuma, vai para o console; e
 * quem não entrou cai na porta da equipe, que é a única entrada sem cliente.
 *
 * Em `yardsuite.com.br` puro (a raiz nua, `ehRaizDoPainel`), "ir para a dele"
 * significa de verdade TROCAR DE HOST agora que a VPS serve vhost curinga -
 * não só prefixar o caminho. Isso é um efeito colateral (troca a página
 * inteira), não um `<Navigate>` comum, daí o `useEffect` abaixo em vez de só
 * devolver o elemento - ver `HandoffSubdominio.tsx` para a troca de sessão
 * entre as duas origens.
 */
function RaizDoPainel() {
  const { status, org, adminPlataforma } = useAuth();
  const slug = useSlugDoCliente();
  const host = typeof window !== 'undefined' ? window.location.host : '';
  const pulaParaSubdominio = !!org?.slug && ehRaizDoPainel(host);

  useEffect(() => {
    if (pulaParaSubdominio && org?.slug) void saltarParaSubdominio(org.slug, '/home');
  }, [pulaParaSubdominio, org?.slug]);

  if (status === 'loading' || pulaParaSubdominio) return null;
  // Em `{slug}.yardsuite.com.br` o subdomínio já é a organização - `comSlug`
  // devolve `/home` sem prefixo nenhum (slug nulo, ver `core/rotas.tsx`).
  // Só sobra para cá quem está em `localhost` (sem vhost curinga) - ali
  // continua sendo caminho por slug mesmo, como sempre foi.
  if (org?.slug) return <Navigate to={comSlug('/home', slug)} replace />;
  if (adminPlataforma) return <Navigate to="/admin" replace />;
  // C5: quarto ramo, explícito. Antes só havia três respostas aqui - tem
  // organização, é da equipe, ou não entrou - e quem entrou, não é da
  // equipe e não tem organização resolvível caía no `<Navigate to="/login">`
  // de baixo. Só que `status` já é `'ready'`: `Login` não tem UM aviso para
  // essa combinação, então a pessoa via a tela de entrar de novo, entrava de
  // novo (a sessão já era válida) e voltava exatamente para cá - o mesmo
  // laço do C1, por outra porta. Hoje o caminho mais provável para chegar
  // aqui é um bug em outro lugar (associação apagada com a sessão de pé,
  // por exemplo) - por isso é tela de estado, não um redirecionamento que
  // finge saber para onde ir.
  if (status === 'ready') return <SemDestino />;
  // Visitante sem login vê a página de entrada da Suite, não a tela de entrar.
  // Quem já entrou nunca chega aqui: cai nos ramos de cima.
  if (status === 'unauthed') return <Navigate to="/site" replace />;
  return <Navigate to="/login" replace />;
}

/** Ver comentário no ramo que usa isto, em `RaizDoPainel`. */
function SemDestino() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="state">
      <div className="stack" style={{ alignItems: 'center' }}>
        <div className="ic-mini" aria-hidden="true">
          <Icone.AlertaTriangulo tamanho={18} />
        </div>
        <h1 className="state__titulo">Não encontramos sua organização</h1>
        <p className="muted" style={{ maxWidth: '46ch' }}>
          Sua conta entrou, mas não está associada a nenhuma organização que possamos abrir. Fale
          com quem administra sua conta, ou tente entrar de novo.
        </p>
        <Button
          variant="secondary"
          onClick={async () => {
            await signOut();
            navigate('/login', { replace: true });
          }}
        >
          Sair e tentar de novo
        </Button>
      </div>
    </div>
  );
}

/**
 * A porta única do suitemail: `yardsuite.com.br/mail`, sem slug de cliente.
 *
 * Qualquer pessoa entra por ali e a URL não muda. Quem entra pela senha da
 * caixa cai na organização dela (ver `entrarPelaSenhaDaCaixa`, em
 * `data/portal.ts`). Os endereços antigos (`{cliente}.yardsuite.com.br/mail`
 * e `mail.yardsuite.com.br`) são levados para cá em `Rotas`, com o caminho
 * que vem depois de `/mail`.
 */

/** Manda o navegador para outro endereço, sem deixar a página antiga no
 *  histórico, e não desenha nada enquanto isso. */
function RedirecionaPara({ destino }: { destino: string }) {
  useEffect(() => {
    window.location.replace(destino);
  }, [destino]);
  return null;
}

function Rotas() {
  const host = typeof window !== 'undefined' ? window.location.host : '';
  const clienteDoSubdominio = clienteDoHost(host);

  // Endereço antigo do suitemail: leva para a porta única, com o caminho. Vem
  // antes de qualquer rota, para que nada do painel monte nesses hosts.
  const destinoDoMail = destinoDoSuitemailNoApex(
    host,
    window.location.pathname,
    window.location.search,
    window.location.hash,
  );
  if (destinoDoMail) return <RedirecionaPara destino={destinoDoMail} />;

  return (
    <Routes>
      {/* A galeria de telas, só em desenvolvimento. Fica antes de tudo porque
          não pertence a superfície nenhuma: não é painel, não é página pública
          de cliente, e não passa por guarda de sessão. */}
      {Galeria && <Route path="/_ds" element={<Galeria />} />}

      {/* Divulgação da Yard: isolada das rotas e das páginas de clientes. */}
      <Route path="/site/*" element={<Site />} />

      {/* público */}
      {/*
        `/login` é a porta da equipe Yard - menos no endereço de um cliente
        (`cima.yardsuite.com.br/login`), onde é a dele (ver a rota, abaixo).
        O cliente entra pelo caminho dele, `/cima`, `/russirussi`, que é o
        endereço que ele recebe e divulga. Foi o subdomínio (`cima.yardsuite.com.br`) que não vingou: a
        hospedagem atual não faz vhost curinga, e sem ele cada cliente novo
        exigia um chamado. O caminho na raiz entrega a mesma tela, com a mesma
        marca, e nasce funcionando para todo cliente que for cadastrado.

        Quem autoriza continua sendo o banco, não a URL: `/login` não dá poder
        de plataforma a ninguém, ela só manda para o console quem já é da
        equipe.
      */}
      {/* A outra ponta do salto de `RaizDoPainel`/`saltarParaSubdominio`
          (`pages/auth/HandoffSubdominio.tsx`): chega aqui já em
          `{cliente}.yardsuite.com.br`, com a sessão na fragment da URL, nunca
          no caminho nem na busca - por isso não precisa de guarda de rota
          nenhuma, o componente mesmo decide o que fazer com o que achar. */}
      <Route path="/_sso" element={<ReceberDeSubdominio />} />
      {/* A ponte que leva a sessão do painel para a sala em `meet.` (ver
          `PonteParaOMeet`): sem guarda, como o `/_sso`. */}
      <Route path="/_ponte-meet" element={<PonteParaOMeet />} />
      {/* Em `{cliente}.yardsuite.com.br`, `/login` é a porta DO CLIENTE. Toda
          saída da sessão e todo "Voltar" das telas de senha mandam para
          `/login`, e no endereço da CIMA isso abria "Entrar na Yard", a
          porta da equipe, com a CIMA na aba do navegador. `/yard` continua
          sendo a da equipe em qualquer host. */}
      <Route path="/login" element={clienteDoSubdominio ? <Login /> : <Login modo="yard" />} />
      <Route path="/yard" element={<Login modo="yard" />} />
      {/* Caminho antigo da porta do cliente. Fica de pé porque já foi mandado
          em e-mail de entrega, e link que a gente mandou não se quebra. */}
      <Route path="/entrar/:slug" element={<Login />} />
      <Route path="/admin/login" element={<Navigate to="/yard" replace />} />
      <Route path="/criar-conta" element={<Signup />} />
      <Route path="/esqueci-senha" element={<ForgotPassword />} />
      <Route path="/redefinir-senha" element={<ResetPassword />} />
      <Route path="/aceitar-convite" element={<AcceptInvite />} />
      <Route path="/criar-organizacao" element={<CreateOrg />} />
      <Route path="/403" element={<Forbidden />} />
      <Route path="/500" element={<ServerError />} />
      <Route path="/suspended" element={<Suspended />} />

      {/* ── Console da Yard ──────────────────────────────────────────────
          Fica fora do slug de propósito: ele não é de cliente nenhum, é a tela
          onde a equipe vê todos. Quem autoriza é `is_platform_admin()` na RLS;
          a tela recusa quem não é, e sem direito o banco não devolve linha. */}
      <Route element={<G><Shell /></G>}>
        <Route path="/admin" element={<G soYard><YardVisao /></G>} />
        <Route path="/admin/clientes" element={<G soYard><YardClientes /></G>} />
        <Route path="/admin/clientes/:id" element={<G soYard><YardCliente /></G>} />
        <Route path="/admin/usuarios" element={<G soYard><YardUsuarios /></G>} />
        <Route path="/admin/equipe" element={<G soYard><YardEquipe /></G>} />
        <Route path="/admin/auditoria" element={<G soYard><YardAuditoria /></G>} />
        <Route path="/admin/recursos" element={<G soYard><YardRecursos /></G>} />
        {/* V26/V43: Perfil e Segurança são da PESSOA (`auth.uid()`), não da
            organização. A equipe Yard sem organização própria não tem slug,
            então `comSlug` deixa `/settings/profile` como está - e sem estas
            duas rotas aqui o caminho não existia fora da área do cliente: o
            administrador da plataforma era o único sem tela para trocar a
            própria senha. A lista de quem passa sem organização é
            `ROTAS_SEM_TENANT`, em `core/RouteGuard.tsx`. */}
        <Route path="/settings/profile" element={<Settings.Profile />} />
        <Route path="/settings/security" element={<Settings.Security />} />
      </Route>

      {/* A raiz manda para a área de quem entrou: o `RaizDoPainel` sabe o slug
          da organização ativa, e a equipe Yard, que não tem uma, vai ao
          console. Os hosts de mail já foram para `/mail` lá em cima. */}
      <Route path="/" element={<RaizDoPainel />} />

      {/* superfícies públicas: sem login, tenant vem do próprio registro */}
      <Route path="/p/:slug" element={<PublicLanding />} />
      <Route path="/f/:formId" element={<PublicForm />} />
      {/* Prévia do embed: endereço público, para mandar por WhatsApp e abrir no
          celular. Carrega o mesmo script que vai no site do cliente. */}
      <Route path="/d/:key" element={<DemoFormulario />} />
      <Route path="/dp/:key" element={<DemoPop />} />

      {/* Superfícies públicas do Bio. Não exigem login: o tenant sai do handle. */}
      <Route path="/b/:handle" element={<BioPage />} />
      <Route path="/b/:handle/q/:slug" element={<QuizPage />} />
      <Route path="/b/:handle/agenda/:slug" element={<AgendaPage />} />
      <Route path="/i/:handle" element={<InfoPage />} />
      <Route path="/s/:handle" element={<ShopPage />} />
      <Route path="/s/:handle/p/:slug" element={<ProdutoPage />} />
      <Route path="/s/:handle/carrinho" element={<CarrinhoPage />} />
      <Route path="/s/:handle/checkout" element={<CheckoutPage />} />
      <Route path="/s/:handle/pedido/:token" element={<PedidoPage />} />
      <Route path="/l/:handle/:slug" element={<LinkRedirect />} />

      {/* Portal · as três superfícies sem sessão. Ver `pages/mail/Publicas.tsx`:
          quem chega aqui é um lead ou alguém que recebeu um link, e nenhuma
          delas lê tabela direto - tudo passa por Edge Function. */}
      <Route path="/agendar/:slug/:tipo" element={<PortalPublico.AgendarPublico />} />
      <Route path="/doc/:token" element={<PortalPublico.DocumentoPublico />} />
      <Route path="/sala/:sala" element={<PortalPublico.SalaPublica />} />

      {/* A Reunião: rota própria, sem casca de Shell nem de suitemail. Serve
          tanto quem está logado (a organização dona da chamada) quanto quem
          só tem o link - a própria tela decide qual dos dois é, olhando se a
          busca por `sala` volta alguma linha (RLS por org_id já resolve
          isso sem checagem extra aqui).

          ErrorBoundary própria (achado ao vivo, 01/09/2026): sem "casca de
          Shell" também quer dizer sem o ErrorBoundary que o Shell dá de
          graça pro resto do produto - um erro aqui (ex.: "Levantar a mão"
          chamando um método da API do Jitsi que não existe) derrubava a
          árvore inteira pra tela branca, no meio de uma chamada de
          verdade. Sala de vídeo é exatamente o lugar errado pra ficar sem
          essa rede - quem está numa reunião não vai abrir o console. */}
      <Route
        path="/reuniao/:sala"
        element={
          <ErrorBoundary area="reuniao">
            <RotaDeReuniao />
          </ErrorBoundary>
        }
      />

      {/* ── Suitemail: a porta única, `yardsuite.com.br/mail` ──────────────
            Antes deste bloco, que vence o `/:slug/*` de baixo. Não tem slug:
            quem entra pela senha da caixa é que define a organização. Os
            endereços antigos de mail e de cliente já foram redirecionados em
            `Rotas`. */}
      <Route path="/mail/*" element={<Suitemail />} />

      {/* ── Área do cliente ──────────────────────────────────────────────
            Duas portas para o mesmo bloco de rotas (`rotasDaAreaDoCliente`,
            abaixo): pelo subdomínio (`cima.yardsuite.com.br/home`, sem slug
            no caminho - o normal a partir de agora) e pelo caminho antigo
            (`/cima/home` - o painel raiz e o `localhost` de desenvolvimento,
            que não têm vhost curinga). As mesmas telas, os mesmos guardas -
            só muda quem prefixa o quê (`core/rotas.tsx`, `comSlug`).

            O índice é a porta de entrada dele, e não o painel: sozinho
            mostra a tela de login com a marca do cliente. */}
      {clienteDoSubdominio && <Route path="/">{rotasDaAreaDoCliente()}</Route>}

      {/* Caminho por slug (`yardsuite.com.br/cima/home`) - SÓ EM `localhost`.
          A VPS com vhost curinga está no ar (`ehRaizDoPainel`/
          `saltarParaSubdominio` já trocam de host sozinhos depois do login),
          então o caminho por path deixou de ser necessário em produção - foi
          pedido explícito matar `yardsuite.com.br/{cliente}/...` fora do dev,
          para não sobrar dois endereços válidos para a mesma conta. Em
          `localhost` continua ligado porque não existe vhost curinga aí, e é
          o único jeito de testar a área do cliente sem subir DNS de teste.
          O `!clienteDoSubdominio` segue de guarda pelo mesmo motivo
          de sempre: `/:slug` (dinâmico) vence `/*` (curinga) na hora de casar
          rota, mesmo vindo depois no arquivo - foi assim que
          `mail.yardsuite.com.br/config` bateu aqui (`slug="config"`) ANTES do
          `<Suitemail />` acima, achado ao vivo testando essa troca. */}
      {!clienteDoSubdominio && ehLocalhost(host) && (
        <Route path="/:slug">{rotasDaAreaDoCliente()}</Route>
      )}

      {/* Em produção (`yardsuite.com.br` puro) o caminho por slug não serve
          a área do cliente - mas `Link`/`useNavigate` de `core/rotas` ainda
          PREFIXAM com o slug da organização ativa, porque não sabem em que
          host estão. A equipe Yard, dentro da conta de um cliente e de volta
          ao console (`yardsuite.com.br/admin`), clicava em "Voltar ao painel"
          (`/home` -> `/cima/home`) e caía em "Página não encontrada": o
          endereço era o do dev, no host de produção. Aqui o link vira o que
          ele quis dizer: um salto para `cima.yardsuite.com.br/home`, pela
          mesma porta que a raiz usa (`saltarParaSubdominio`). Só salta para
          organização que a sessão conhece (ativa ou com vínculo); slug
          estranho continua sendo "não encontrado", porque não é. */}
      {!clienteDoSubdominio && !ehLocalhost(host) && (
        <Route path="/:slug/*" element={<SaltarParaOCliente />} />
      )}

      {/*
        Sobra de tudo: caminho que não bateu em nada acima. Em
        `{slug}.yardsuite.com.br` isso normalmente nem chega aqui - o
        `index`/`*` de dentro de `rotasDaAreaDoCliente()` já cobre o
        subdomínio inteiro -, mas em `yardsuite.com.br` (raiz, sem cliente
        nenhum) ainda sobra caminho nenhum-a-ver para cair aqui.
      */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/**
 * O bloco de rotas do painel do cliente, comum aos dois modos de endereço
 * (ver comentário acima). Escrito como função, não componente: `<Routes>`
 * só reconhece `<Route>`/`<React.Fragment>` como filho de verdade - chamar
 * `rotasDaAreaDoCliente()` aqui dentro do JSX resolve a função ANTES de
 * `Routes` olhar para os filhos, então o Fragment que ela devolve entra na
 * árvore como se tivesse sido escrito ali. Um componente (`<AreaDoCliente/>`)
 * não funcionaria: `Routes` não renderiza componentes para descobrir o que
 * tem dentro, só reconhece os elementos literais.
 */
function rotasDaAreaDoCliente() {
  return (
    <>
      <Route index element={<Login exigeCliente />} />

      {/*
        yard suitemail · outro produto, por isso fora do `<Shell />`.
        Continua existindo aqui, além de `mail.yardsuite.com.br` (acima):
        é o caminho antigo (`/{cliente}/mail`, ainda em uso por quem tem link
        salvo) e o único jeito de testar o suitemail em `localhost`, que não
        tem o subdomínio fixo. Repare que não há `<G app="MAIL">` aqui: o
        acesso não é a permissão do Suite, é a caixa vinculada. Quem tem
        conta no Suite e nenhuma caixa vê a tela que diz isso, e não um 403.
        A decisão de escopo está em `docs/migracao/E-suitemail-arquitetura.md`.
      */}
      {/* Ata é documento da conta; não exige possuir uma caixa de e-mail. */}
      <Route path="mail/reuniao/:id/resumo" element={<G><ErrorBoundary area="ata"><ResumoDaReuniao /></ErrorBoundary></G>} />
      <Route path="mail/*" element={<Suitemail />} />

        <Route element={<G><Shell /></G>}>
          <Route path="home" element={<Home />} />

        {/*
          AS CINCO DE AUTO-SERVIÇO NÃO SÃO GUARDADAS POR PERMISSÃO NA ROTA.

          `ROTAS-CONFIGURACOES.md`, seção 5, é explícito sobre o estado "sem
          permissão" delas: "os campos ficam visíveis e desabilitados, com uma
          linha dizendo quem pode editar - nunca a aba some inteira, porque
          sumir ensinaria que o recurso não existe".

          Mandar para /403, como era antes, ensina exatamente isso: a pessoa
          conclui que a conta não tem Marca, quando na verdade tem e ela é que
          não pode mexer. Quem decide agora é a própria aba, que desenha o
          formulário travado e diz a quem pedir - a escrita continua barrada no
          banco pela RLS, que é onde a proibição tem de morar de qualquer jeito.

          As SEIS da equipe Yard continuam com `soYard` na rota: ali a regra é
          outra ("aba nem aparece", seção 4), e o bloqueio é de verdade.
        */}
        <Route path="settings" element={<VaiPara to="/settings/profile" />} />
        <Route path="settings/profile" element={<Settings.Profile />} />
        <Route path="settings/members" element={<Settings.Members />} />
        <Route path="settings/branding" element={<Settings.Branding />} />
        <Route path="settings/organization" element={<Settings.OrganizationSettings />} />
        <Route path="settings/security" element={<Settings.Security />} />
        <Route path="settings/apps" element={<G soYard><Settings.Apps /></G>} />
        <Route path="settings/apikeys" element={<G soYard><Settings.ApiKeys /></G>} />
        <Route path="settings/integrations" element={<G soYard><Settings.Integrations /></G>} />
        <Route path="settings/flags" element={<G soYard><Settings.Flags /></G>} />
        <Route path="settings/billing" element={<G soYard><Settings.Billing /></G>} />
        <Route path="settings/audit" element={<G soYard><Settings.AuditScreen /></G>} />

        {/* MKT */}
        <Route path="mkt" element={<VaiPara to="/mkt/dashboard" />} />
        <Route path="mkt/dashboard" element={<G app="MKT" permission="mkt.contact:read"><Mkt.Dashboard /></G>} />
        <Route path="mkt/contacts" element={<G app="MKT" permission="mkt.contact:read"><Mkt.Contacts /></G>} />
        <Route path="mkt/contacts/import" element={<G app="MKT" permission="mkt.contact:create"><ImportWizard /></G>} />
        <Route path="mkt/contacts/:id" element={<G app="MKT" permission="mkt.contact:read"><Mkt.ContactDetail /></G>} />
        <Route path="mkt/segments" element={<G app="MKT" permission="mkt.contact:read"><Mkt.Segments /></G>} />
        <Route path="mkt/landing-pages" element={<G app="MKT" permission="mkt.landingpage:read"><Mkt.LandingPages /></G>} />
        <Route path="mkt/landing-pages/:id/edit" element={<G app="MKT" permission="mkt.landingpage:update"><LandingEditor /></G>} />
        <Route path="mkt/automations/:id" element={<G app="MKT" permission="mkt.automation:read"><AutomationViewer /></G>} />
        <Route path="mkt/automations/:id/edit" element={<G app="MKT" permission="mkt.automation:update"><AutomationBuilder /></G>} />
        <Route path="mkt/forms" element={<G app="MKT" permission="mkt.landingpage:read"><Mkt.Forms /></G>} />
        <Route path="mkt/forms/:id/edit" element={<G app="MKT" permission="mkt.landingpage:update"><FormBuilder /></G>} />
        <Route path="mkt/popups" element={<G app="MKT" permission="mkt.landingpage:read"><Mkt.Popups /></G>} />
        <Route path="mkt/emails" element={<G app="MKT" permission="mkt.email:send"><Mkt.Emails /></G>} />
        <Route path="mkt/emails/:id/edit" element={<G app="MKT" permission="mkt.email:send"><EmailBuilder /></G>} />
        <Route path="mkt/emails/:id/relatorio" element={<G app="MKT" permission="mkt.report:read"><EmailRelatorio /></G>} />
        <Route path="mkt/settings/sender" element={<G app="MKT" permission="mkt.email:send"><SenderSettings /></G>} />
        <Route path="mkt/automations" element={<G app="MKT" permission="mkt.automation:read"><Mkt.Automations /></G>} />
        <Route path="mkt/reports" element={<G app="MKT" permission="mkt.report:read"><Mkt.Reports /></G>} />
        <Route path="mkt/jornadas" element={<G app="MKT" permission="mkt.report:read"><Jornadas /></G>} />
        <Route path="mkt/jornadas/:id" element={<G app="MKT" permission="mkt.contact:read"><JornadaDoContato /></G>} />
        <Route path="mkt/settings/scoring" element={<G app="MKT" permission="mkt.automation:update"><Mkt.MktSettings /></G>} />

        {/* E-MAILS · biblioteca (ROTAS-EMAILS.md). Família roxo, mesmo app MKT -
            "Campanha" e "Recuperação de senha" aqui dentro só linkam de volta
            para `mkt/emails` e para o painel do Supabase Auth. */}
        <Route path="emails" element={<VaiPara to="/emails/biblioteca" />} />
        {/* A biblioteca é Marketing: entra na moldura do módulo (as abas do
            MKT, família roxo), como Jornadas. Sem a moldura ela abria "solta"
            na casca, com o lockup "yard suite" e sem o menu do produto. */}
        <Route path="emails/biblioteca" element={<G app="MKT" permission="mkt.email:send"><MktFrame><EmailsBiblioteca /></MktFrame></G>} />
        <Route path="emails/historico" element={<G app="MKT" permission="mkt.email:send"><MktFrame><EmailsHistorico /></MktFrame></G>} />
        <Route path="emails/assinaturas" element={<G app="MKT" permission="mkt.email:send"><MktFrame><EmailsAssinaturas /></MktFrame></G>} />
        <Route path="emails/:templateId/editar" element={<G app="MKT" permission="mkt.email:send"><MktFrame><EmailsEditor /></MktFrame></G>} />
        <Route path="emails/:templateId/teste" element={<G app="MKT" permission="mkt.email:send"><MktFrame><EmailsTeste /></MktFrame></G>} />

        {/* CRM */}
        {/* Uma rota só (`ROTAS-CRM.md`, seção 0 e 2): o módulo ativo é a
            âncora da URL (#funil, #lista, #contatos...), nunca um caminho
            novo, e abrir um negócio/contato/empresa é gaveta por querystring
            (?negocio=, ?contato=, ?empresa=) sobre esta mesma rota. Nenhum
            dos nove módulos tem `<Route>` próprio - eles vivem dentro de
            `<Negocios />`, ver `pages/crm/Negocios.tsx`. */}
        <Route path="crm" element={<G app="CRM" permission="crm.deal:read"><Negocios /></G>} />

        {/* FLW */}
        <Route path="flw" element={<G app="FLW"><EntradaDoFlow /></G>} />
        {/* S6: 0066 (semeia flw.queue:supervise) confirmada aplicada em
            produção em 01/09/2026 - religado ao gate certo (era
            flw.report:read desde 22/08, enquanto a migração não rodava
            contra o projeto real). */}
        <Route path="flw/dashboard" element={<G app="FLW" permission="flw.queue:supervise"><Flw.FlwDashboard /></G>} />
        <Route path="flw/inbox" element={<G app="FLW" permission="flw.conversation:handle"><Flw.Inbox /></G>} />
        <Route path="flw/chatbots" element={<G app="FLW" permission="flw.chatbot:manage"><Flw.Chatbots /></G>} />
        <Route path="flw/chatbots/:id" element={<G app="FLW" permission="flw.chatbot:manage"><Flw.ChatbotEditor /></G>} />
        <Route path="flw/chatbots/:id/testar" element={<G app="FLW" permission="flw.chatbot:manage"><Flw.ChatbotTestar /></G>} />
        <Route path="flw/channels" element={<G app="FLW" permission="flw.channel:connect"><Flw.Channels /></G>} />
        <Route path="flw/coexistencia" element={<G app="FLW" permission="flw.channel:connect"><Coexistencia /></G>} />
        <Route path="flw/templates" element={<G app="FLW" permission="flw.campaign:manage"><FlwCfg.Templates /></G>} />
        <Route path="flw/campaigns" element={<G app="FLW" permission="flw.campaign:manage"><FlwCfg.Campaigns /></G>} />
        <Route path="flw/reports" element={<G app="FLW" permission="flw.report:read"><Flw.FlwReports /></G>} />
        <Route path="flw/settings/routing" element={<G app="FLW" permission="flw.channel:connect"><FlwCfg.Routing /></G>} />
        <Route path="flw/settings/ai-agents" element={<G app="FLW" permission="flw.chatbot:manage"><FlwCfg.AiAgents /></G>} />
        <Route path="flw/settings/quick-replies" element={<G app="FLW" permission="flw.chatbot:manage"><FlwCfg.QuickReplies /></G>} />
        <Route path="flw/materiais" element={<Navigate to="/crm#tabelas" replace />} />

        {/* POP · Flow Pop-up */}
        <Route path="pop" element={<VaiPara to="/pop/dashboard" />} />
        {/* P9: `PopDashboard` só lê (`pop_desempenho`, `conversations`,
            `pop_atendentes`) - não tem ação de configurar nada. Exigir
            `pop.widget:manage` (que só manager+ tem) mandava `agent` e
            `viewer` direto para o 403 ao clicar no cartão do produto na
            Home, mesmo os dois tendo `pop.report:read` (POP_OPERATE,
            core/permissions.ts). */}
        <Route path="pop/dashboard" element={<G app="POP" permission="pop.report:read"><Pop.PopDashboard /></G>} />
        <Route path="pop/aparencia" element={<G app="POP" permission="pop.widget:manage"><Pop.PopAparencia /></G>} />
        <Route path="pop/atendentes" element={<G app="POP" permission="pop.widget:manage"><Pop.PopAtendentes /></G>} />
        <Route path="pop/mensagens" element={<G app="POP" permission="pop.widget:manage"><Pop.PopMensagens /></G>} />
        <Route path="pop/instalacao" element={<G app="POP" permission="pop.widget:manage"><Pop.PopInstalacao /></G>} />
        <Route path="pop/conversas" element={<G app="POP" permission="pop.conversation:handle"><Pop.PopConversas /></G>} />
        <Route path="pop/relatorio" element={<G app="POP" permission="pop.report:read"><Pop.PopRelatorio /></G>} />

        {/* MAIL · o suitemail saiu daqui. As rotas dele ficam fora do `<Shell />`,
            logo abaixo: outro produto, outra casca, outra porta. */}
        <Route path="bio" element={<G app="BIO" permission="bio.site:read"><Bio.Sites /></G>} />
        <Route path="bio/sites/:id" element={<G app="BIO" permission="bio.site:read"><Bio.SiteOverview /></G>} />
        <Route path="bio/sites/:id/editor" element={<G app="BIO" permission="bio.site:update"><Bio.SiteEditor /></G>} />
        <Route path="bio/sites/:id/tema" element={<G app="BIO" permission="bio.site:update"><Bio.SiteTema /></G>} />
        <Route path="bio/sites/:id/links" element={<G app="BIO" permission="bio.site:update"><Bio.SiteLinks /></G>} />
        <Route path="bio/sites/:id/produtos" element={<G app="BIO" permission="bio.site:read"><BioCom.SiteProdutos /></G>} />
        <Route path="bio/sites/:id/pedidos" element={<G app="BIO" permission="bio.order:read"><BioCom.SitePedidos /></G>} />
        <Route path="bio/sites/:id/quizzes" element={<G app="BIO" permission="bio.quiz:manage"><BioCom.SiteQuizzes /></G>} />
        <Route path="bio/sites/:id/quizzes/:quizId" element={<G app="BIO" permission="bio.quiz:manage"><BioCom.QuizBuilder /></G>} />
        {/* Ler as respostas é `bio.report:read`, e não `bio.quiz:manage`: quem
            acompanha os leads não precisa poder mexer nos pesos da conversa. */}
        <Route path="bio/sites/:id/quizzes/:quizId/relatorio" element={<G app="BIO" permission="bio.report:read"><BioCom.QuizRelatorio /></G>} />
        <Route path="bio/sites/:id/agenda" element={<G app="BIO" permission="bio.booking:manage"><BioCom.SiteAgenda /></G>} />
        <Route path="bio/sites/:id/depoimentos" element={<G app="BIO" permission="bio.site:update"><BioCom.SiteDepoimentos /></G>} />
        <Route path="bio/sites/:id/imoveis" element={<G app="BIO" permission="bio.site:update"><BioCom.SiteImoveis /></G>} />
        <Route path="bio/sites/:id/relatorios" element={<G app="BIO" permission="bio.report:read"><Bio.SiteRelatorios /></G>} />
        <Route path="bio/sites/:id/config" element={<G app="BIO" permission="bio.site:update"><Bio.SiteConfig /></G>} />

      <Route path="*" element={<NotFound />} />
        </Route>
    </>
  );
}
